import { GetStoreConfigPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_config/v1/store_config_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeError } from '@stripe/stripe-js'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  createPaymentDetailsFormSchema,
  PaymentDetailsFormSchema,
} from '@/components/checkout/types'
import { FormErrors } from '@/components/fields/form-errors'
import { StripeErrorAlert } from '@/components/payment/stripe/stripe-error-alert'
import { Button } from '@/components/ui/button'

import { ContactDetailsForm } from './contact-details-form'

export type Props = {
  storeConfigData?: GetStoreConfigPublicResponse
  submitText?: string
  siteSlug: string
  defaultValues: PaymentDetailsFormSchema
  getSetupParams: () => {
    clientSecret: string | undefined
    returnUrl: string
  }
  onSetupInitiated?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
  onSetupConfirmed?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
}

export const SetupForm = ({
  submitText = 'Save payment details',
  defaultValues,
  onSetupInitiated,
  onSetupConfirmed,
  getSetupParams,
}: Props) => {
  const { t } = useTranslation()

  const [stripeError, setStripeError] = useState<StripeError | undefined>(
    undefined,
  )
  const [isLoading, setIsLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()

  const form = useForm<PaymentDetailsFormSchema>({
    resolver: zodResolver(
      createPaymentDetailsFormSchema({ requiresShipping: false }),
    ),
    mode: 'all',
    defaultValues,
  })

  const handleError = (error: StripeError) => {
    setIsLoading(false)
    setStripeError(error)
  }

  const submitHandler = useCallback(
    async (input: PaymentDetailsFormSchema) => {
      const { clientSecret, returnUrl } = getSetupParams()

      if (!stripe || !elements || !clientSecret || !returnUrl) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return
      }

      setIsLoading(true)

      if (onSetupInitiated) {
        await onSetupInitiated({ input })
      }

      const { error: submitError } = await elements.submit()

      if (submitError) {
        handleError(submitError)
        return
      }

      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
        },
      })

      if (error) {
        handleError(error)
      } else {
        if (onSetupConfirmed) {
          await onSetupConfirmed({ input })
        }
        setIsLoading(false)
      }
    },
    [getSetupParams, stripe, elements, onSetupInitiated, onSetupConfirmed],
  )

  return (
    <form onSubmit={form.handleSubmit(submitHandler)}>
      <div className="mb-4 font-semibold">{t('enter_payment_detail')}</div>

      <div className="mb-4">
        <ContactDetailsForm form={form} />
      </div>

      <PaymentElement />

      <FormErrors errors={form.formState.errors} />

      {stripeError && (
        <StripeErrorAlert className="mt-12" error={stripeError} />
      )}

      <Button
        disabled={isLoading || !stripe || !elements}
        className="mt-4 w-full"
        loading={isLoading}
      >
        {submitText}
      </Button>
    </form>
  )
}
